import LeagueManagementList from "../../organizer/components/LeagueManagementList";
import { useCallback, useEffect, useState } from "react";
import type { LeagueSummary } from "@tnp/contracts";
import Button from "../../../../shared/components/Button";
import EmptyState from "../../../../shared/components/EmptyState";
import Toast from "../../../../shared/components/Toast";
import { listLeagues } from "../services/leagueService";
import { getLeagueTexts } from "../leagueTexts";
import { useLeagueResource } from "../hooks/useLeagueResource";
import LeagueCard from "./LeagueCard";
import LeagueFeedback from "./LeagueFeedback";
import LeagueLoadError from "./LeagueLoadError";

export default function LeagueDirectory({ token, onEdit, onDelete }: { token?: string; onEdit?: (id: string) => void; onDelete?: (league: LeagueSummary) => void }) {
  const t = getLeagueTexts();
  const load = useCallback(() => listLeagues(token), [token]);
  const resource = useLeagueResource(load);
  const [items, setItems] = useState<LeagueSummary[]>([]);
  const [next, setNext] = useState<number | null>(null);
  const [moreLoading, setMoreLoading] = useState(false);
  const [error, setError] = useState("");
  useEffect(() => { setItems(resource.data?.items || []); setNext(resource.data?.nextOffset ?? null); }, [resource.data]);
  async function more() {
    if (next === null || moreLoading) return;
    setMoreLoading(true);
    try {
      const result = await listLeagues(token, next);
      setItems(previous => [...previous, ...result.items.filter(item => !previous.some(existing => existing.id === item.id))]);
      setNext(result.nextOffset);
    } catch (reason) { setError(reason instanceof Error ? reason.message : t.loadError); }
    finally { setMoreLoading(false); }
  }
  if (resource.loading) return <LeagueFeedback>{t.loading}</LeagueFeedback>;
  if (resource.error) return <LeagueLoadError error={resource.error} retry={resource.retry} />;
  return <>
    {!items.length && (
      <EmptyState title={token ? t.emptyOwn : t.empty} iconName="emoji_events" />
    )}
    {items.length > 0 && (onEdit
      ? <LeagueManagementList items={items} onEdit={onEdit} onDelete={onDelete} />
      : <div className="league-grid league-public-grid">{items.map(item => <LeagueCard key={item.id} league={item} management={Boolean(token)} />)}</div>)}
    {next !== null && <Button onClick={more} disabled={moreLoading}>{moreLoading ? t.loading : t.more}</Button>}
    {error && <Toast message={error} variant="error" onClose={() => setError("")} />}
  </>;
}
